import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';

import Layout from '../../components/Layout/Layout';
import InputComponent from '../../components/InputComponent';
import ButtonComponent from '../../components/ButtonComponent';
import TableComponent, { OperationsCell } from '../../components/TableComponent';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { Parameter } from '../../models/Parameter';
import { CheckListModelDTO } from '../../models/CheckListModel/CheckListModelDTO';
import { CheckListModelService } from '../../services/CheckListModelService';
import Routes from '../../helpers/Routes';

export default function CheckListModelIndex() {
    const intl = useIntl();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pageSizes: number[] = [5, 10, 15, 30];
    const service = new CheckListModelService();

    const [data, setData] = useState<CheckListModelDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes, 10));

    const [goToPage, setGoToPage] = useState<number>();

    const [title, setTitle] = useState<string>(Utilities.LoadParameterFromURLQuery("title", "string", ""));
    const [brand, setBrand] = useState<string>(Utilities.LoadParameterFromURLQuery("brand", "string", ""));
    const [model, setModel] = useState<string>(Utilities.LoadParameterFromURLQuery("model", "string", ""));
    const [active, setActive] = useState<string>(Utilities.LoadParameterFromURLQuery("active", "string", ""));

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => OperationsCell(props.cell.row.original.id, "checkListModels"),
            },
            {
                Header: <FormattedMessage id="Screens.CheckListModel.Index.Table.Title" />,
                accessor: "title",
            },
            {
                Header: <FormattedMessage id="Screens.CheckListModel.Index.Table.Brand" />,
                accessor: "brand",
            },
            {
                Header: <FormattedMessage id="Screens.CheckListModel.Index.Table.Model" />,
                accessor: "model",
            },
            {
                Header: <FormattedMessage id="Screens.CheckListModel.Index.Table.Active" />,
                accessor: "active",
                Cell: ({ value }: { value: boolean }) => (
                    <>{value ? "Ativo" : "Inativo"}</>),
            },
        ],
        [intl]
    );

    const mountSearchParameters = (): Parameter[] => {
        const parameters: Parameter[] = [];
        if (title.trim() !== "") parameters.push(new Parameter("title", title));
        if (brand.trim() !== "") parameters.push(new Parameter("brand", brand));
        if (model.trim() !== "") parameters.push(new Parameter("model", model));
        if (active.trim() !== "") parameters.push(new Parameter("active", active));

        return parameters;
    };

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {

        let searchUrl: string = `?page=${page}&pageSize=${pageSize}`;
        searchUrl += Utilities.AddParamToQueryIfValue("title", "string", title);
        searchUrl += Utilities.AddParamToQueryIfValue("brand", "string", brand);
        searchUrl += Utilities.AddParamToQueryIfValue("model", "string", model);
        searchUrl += Utilities.AddParamToQueryIfValue("active", "string", active);

        if (sortBy && sortBy.length > 0) {
            searchUrl += Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id);
            searchUrl += Utilities.AddParamToQueryIfValue("sortOrder", "string", sortBy[0].desc ? "DESC" : "ASC");
        }

        navigate(`/checkListModels${searchUrl}`);
        setLoading(true);

        const response = await service.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );



        if (!response.success) {
            Toast.Show("error", response.message);
            setData([]);
            setPageCount(0);
            setLoading(false);
            return;
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }

        setLoading(false);
    };

    const onRemoveFiltersClick = () => {
        setTitle("");
        setBrand("");
        setModel("");
        setActive("");
        setReloadSearch(true);
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    };

    const onButtonClick = () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(1, currentPageSize ?? 10, currentSorting);
    };

    useEffect(() => {
        fetchData(page, pageSize, initialSortBy);
    }, []);

    useEffect(() => {
        if (reloadSearch) {
            fetchData(1, currentPageSize ?? 10, currentSorting);
            setReloadSearch(false);
        }
    }, [reloadSearch]);

    const onNewClick = () => {
        navigate(Routes.checkListModels.create);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        <FormattedMessage id="Screens.CheckListModel.Index.Title" />
                    </span>
                </div>
            </div>

                <Row>
                    <Col xl={2} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em" }}>
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Screens.CheckListModel.Index.Button.Create" })}
                            onClick={onNewClick}
                        />
                    </Col>
                </Row>

                <div className="pageBody">
                    <Row>
                        <InputComponent
                            xl={2} lg={3} md={5} sm={12} xs={12}
                            type="text"
                            label={intl.formatMessage({ id: "Screens.CheckListModel.Index.Filters.Title" })}
                            value={title}
                            onEnter={onEnter}
                            onChange={setTitle}
                        />
                        <InputComponent
                            xl={2} lg={3} md={5} sm={12} xs={12}
                            type="text"
                            label={intl.formatMessage({ id: "Screens.CheckListModel.Index.Filters.Brand" })}
                            value={brand}
                            onEnter={onEnter}
                            onChange={setBrand}
                        />
                        <InputComponent
                            xl={2} lg={3} md={5} sm={12} xs={12}
                            type="text"
                            label={intl.formatMessage({ id: "Screens.CheckListModel.Index.Filters.Model" })}
                            value={model}
                            onEnter={onEnter}
                            onChange={setModel}
                        />
                        <Col xl={2} lg={3} md={5} sm={12} xs={12}>
                            <label className="form-label">
                                <FormattedMessage id="Screens.CheckListModel.Index.Filters.Active" />
                            </label>
                            <div className="position-relative">
                                <select
                                    className="form-control"
                                    style={{
                                        fontSize: '14px',
                                        cursor: "pointer",
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='gray' class='bi bi-caret-down-fill' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658c-.566-.65-.106-1.658.753-1.658h9.592c.86 0 1.32 1.008.753 1.658l-4.796 5.482a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right .75rem center',
                                        backgroundSize: '1em',
                                        appearance: 'none',
                                        paddingRight: '2em'
                                    }}
                                    value={active}
                                    onChange={(e) => setActive(e.target.value)}
                                >
                                    <option value="">Escolher estado</option>
                                    <option value="true">Ativo</option>
                                    <option value="false">Inativo</option>
                                </select>
                            </div>
                        </Col>

                    </Row>



                    <Row>
                        <Col xl={3} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                onClick={onButtonClick}
                            />
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                onClick={onRemoveFiltersClick}
                            />
                        </Col>
                    </Row>

                    <div className="pageComponent" style={{ width: "100%" }}>
                        {data.length === 0 ? (
                            <div className="no-data-message">
                                <FormattedMessage id="Screens.CheckListModel.NoDataMessage" defaultMessage="Não existem checklists disponíveis" />
                            </div>
                        ) : (
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        )}
                    </div>

                </div>
        </Layout>
    );
}
